import { Col, ELanguage, Hooks, Popup, Row, UploadResumeModal, useHog, Utils } from '@microfrontends/react-components'
import { memo, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'
import { ERefreshProfile, TProfileSetting, useStore } from '../../store'
import ProfileSettings from '../Page/ProfileSettings'
import translations from '../Page/ProfileSettings/translations'
import { ACTION_SET_USER, ACTION_SHOW_SETTING_PROFILE } from '../../store/actions'
import { ENABLE_JOB_FUNCTION } from '../Global/MainLayout'
import { initialDataProvider } from '@microfrontends/app-shell-v2'
import { getLocalizedOptions } from '../../helpers/util'
import { nationalityList, ResumeType } from '../Page/ProfileSettings/constants'
import { apiGetRefresh, apiGetResumeInfo, apiMakeRefresh, apiUpdateResumeSetting } from '../Page/ProfileSettings/services'
import syncProfileSettingInfoStore from '../../helpers/syncData/syncProfileSettingInfoStore'
import { iniStore, ProfileSettingProvider, TState } from '../../store/ProfileSetting'
import { SUCCESS_CODE } from '../Global/MainLayout/SideNavigation/constant'
import { makePofileOpenToWork } from '../../services/openToWork'
import { ModelError } from '@microfrontends/react-components/components/services'
import { useRouter } from 'next/router'
import { convertDateTime } from '../../helpers/date'
import { WarningSearchableModal } from '../Page/ProfileSettings/MakeProfileSearchable/makeProfileSearchable.css'

interface IProps {}

const PROFILE_SETTINGS_TAB = 'profile-settings'

const DELAY_TIME_TO_SHOW_POPUP = 1000

function ProfileSettingContainer({ children }: PropsWithChildren<IProps>) {
    const router = useRouter()

    const { state, dispatch } = useStore()
    const { langCode, user, profileSettingInfo } = state
    const isOpen = state.isShowPopupSetting
    const userProfileInfo = state.profileInfo
    const {
        resumeSetting,
        resumeSettingOption,
        searchableStatus,
        unContactable,
        resumeSetting: { profileInfo }
    } = profileSettingInfo
    const { tracking } = useHog()
    const t = Utils.createTranslator({ translations, currentLanguage: langCode, defaultLanguage: ELanguage.EN })

    const [settingState, disPatchSettingState] = useState<TState>(iniStore.state)

    const [isShowUpload, setIsShowUpload] = useState(false)
    const [isShowSetting, setIsShowSetting] = useState(false)
    const [isShowUncontactable, setIsShowUncontactable] = useState<boolean>(false)

    const [attachCVStep, setAttachCVStep] = useState<number>(1)
    const [isOpenUploadCV, setIsOpenUploadCV] = useState(false)

    const settingRef = useRef<TState>()
    const timer = useRef<NodeJS.Timeout>()
    const isFromUploadResumeRef = useRef<boolean>(false)

    const jobLevelList = getLocalizedOptions(initialDataProvider.getLevels(), langCode)
    const locationList = getLocalizedOptions(initialDataProvider.getLocations(), langCode)
    const nationalityOptions = nationalityList(langCode)
    const countryOptions = getLocalizedOptions(initialDataProvider.getCountries(), langCode)
    const cityOptions = getLocalizedOptions(initialDataProvider.getLocations(), langCode)
    const districtOptions = initialDataProvider.getDistricts()

    const initDataMakeSearchable = {
        data: {
            resumes: [...[resumeSetting.profileInfo], ...resumeSetting.attachInfo],
            profileSetting: resumeSettingOption as Partial<TProfileSetting>,
            searchableStatus: searchableStatus as 0 | 1,
            unContactable: unContactable as 0 | 1
        },
        accessToken: user?.accessToken ?? '',
        langCode,
        baseMsUrl: process.env.NEXT_PUBLIC_MS_BASE_PATH ?? '',
        isUpdateCookies: false
    }
    const [_, handleSearchable, priorityResume] = Hooks.useMakeSearchable(initDataMakeSearchable)

    const doRefreshProfile = async () => {
        try {
            if (!user?.accessToken) return null
            const body = {
                status: settingState.statusRefresh !== ERefreshProfile.Unset ? 1 : 0,
                type: settingState.statusRefresh
            }
            const res = await apiMakeRefresh(user.accessToken, langCode, body)
            if (res.code === SUCCESS_CODE) {
                return body
            }
            return null
        } catch (error) {
            console.error(error)
            return null
        }
    }

    const opentoWork = async () => {
        try {
            if (!user?.accessToken) return null
            const res = await makePofileOpenToWork({
                isOpenToWork: settingState.isOpenToWork ? 1 : 0,
                langCode: langCode,
                accessToken: user?.accessToken
            })
            return res
        } catch (error) {
            console.error(error)
            return null
        }
    }

    const onClose = () => {
        dispatch({ type: ACTION_SHOW_SETTING_PROFILE, payload: false })
        setIsShowSetting(false)
        setIsShowUpload(false)
        if (isFromUploadResumeRef.current) {
            window.location.reload()
            return
        }
    }

    const getResumeInfo = async () => {
        try {
            const resumeInfoResponse = await apiGetResumeInfo(user?.accessToken || '')
            if (
                resumeInfoResponse !== undefined &&
                resumeInfoResponse.data !== undefined &&
                resumeInfoResponse.data.attributes !== undefined
            ) {
                syncProfileSettingInfoStore(resumeInfoResponse, profileSettingInfo, dispatch)
            }
        } catch (error) {
            console.error(error)
        }
    }

    const onSyncDataToStoreWhenDoneStep = async (step: number, value?: any) => {
        await getResumeInfo()
        if (step === 3) {
            // Save Job title to user
            dispatch({
                type: ACTION_SET_USER,
                payload: {
                    ...user,
                    jobTitle: value.jobTitle
                }
            })
            setIsShowSetting(true)
        }
    }

    const onClickAllowSearchable = async () => {
        try {
            if (!settingState.resumeId) {
                Utils.showToast(t('Error'), 'Missing resume info', 'error')
                onClose()
                return
            }
            const doSearchable = await handleSearchable(true, settingState.resumeId)
            if (!doSearchable || doSearchable instanceof ModelError) {
                if (profileSettingInfo.unContactable) {
                    onClose()
                    setIsShowUncontactable(true)
                    return
                }
                Utils.showToast(t('Error'), t('Something went wrong with your change!'), 'error')
                onClose()
                return
            }

            const promiseRefreshProfile = doRefreshProfile()
            const promiseOpenToWork = opentoWork()
            const response = await Promise.allSettled([promiseRefreshProfile, promiseOpenToWork])
            const isHasErr = response.filter((e) => e.status === 'rejected')?.length > 0

            if (!isHasErr) {
                settingRef.current = {
                    resumeId: settingState.resumeId,
                    isOpenToWork: settingState.isOpenToWork,
                    statusRefresh: settingState.statusRefresh,
                    expiredDate: settingState.expiredDate,
                    isTurnOnRefresh: settingState.statusRefresh !== ERefreshProfile.Unset
                }
                setTimeout(() => getResumeInfo(), 1000)

                Utils.showToast(t('Success'), t('Your changes has been saved successfully!'))

                const statusRefresh =
                    response[0].status === 'fulfilled' ? response[0].value?.type : resumeSettingOption.chooseRefreshType

                const chooseCV = profileInfo.id === settingState.resumeId ? ResumeType.Profile : ResumeType.AttachCV

                apiUpdateResumeSetting(user?.accessToken || '', langCode, {
                    chooseCV: profileInfo.id === settingState.resumeId ? ResumeType.Profile : ResumeType.AttachCV,
                    chooseRefreshType: statusRefresh,
                    resumeId: settingState.resumeId
                })

                tracking({
                    eventName: 'js_profile_management:searchable_turn_on_success',
                    properties: {
                        isOnlineProfile: chooseCV === 'profile',
                        isAttachment: chooseCV === 'attach-cv',
                        resumeId: settingState.resumeId
                    }
                })
                if (response[0].status === 'fulfilled' && settingState.statusRefresh !== ERefreshProfile.Unset) {
                    tracking({
                        eventName: 'js_profile_management:refresh_turn_on_success',
                        properties: {
                            typeRefresh: settingState.statusRefresh === 1 ? 'daily' : 'weekly',
                            resumeId: settingState.resumeId,
                            cvType: chooseCV
                        }
                    })
                }
                if (response[1].status === 'fulfilled' && settingState.isOpenToWork) {
                    tracking({
                        eventName: 'js_profile_management:urgent_turn_on_success'
                    })
                }

                onClose()
                return
            }
            Utils.showToast(t('Error'), t('Something went wrong with your change!'), 'error')
            onClose()

            return
        } catch (error) {
            console.error(error)
        }
    }

    const memoConfigPopupUpload = useMemo(() => {
        const config = {
            showUploadResume: false,
            step: 1
        }
        if (!isOpen || !profileSettingInfo?.meta) {
            return config
        }

        const { meta, searchableStatus } = profileSettingInfo
        // Don't have searchable status
        if (!meta.isAttach && searchableStatus === 0) {
            config.showUploadResume = true
        } else if (meta.isAttach && !meta.isContactInformation) {
            config.showUploadResume = true
            config.step = 2
        } else if (meta.isAttach && meta.isContactInformation && !meta.isWorkingPreferenceWeb) {
            config.showUploadResume = true
            config.step = 3
        } else {
            config.showUploadResume = false
            config.step = 1
        }

        return config
    }, [profileSettingInfo, isOpen])

    const isDisableSearchableButton = useMemo(() => {
        let isEqual = true
        if (settingRef.current?.isOpenToWork !== settingState.isOpenToWork) {
            isEqual = false
        }
        if (settingRef.current?.resumeId !== settingState.resumeId) {
            isEqual = false
        }
        if (settingRef.current?.statusRefresh !== settingState.statusRefresh) {
            isEqual = false
        }

        const { attachInfo, profileInfo } = profileSettingInfo.resumeSetting
        const isDisabledSearchableOnline = profileInfo.isSearchable === 0
        const filteredSearchableAttach = attachInfo.filter((item) => item.isSearchable === 1)
        // Do not disable primary button when
        // There is no searchable is turned on
        if (filteredSearchableAttach.length === 0 && isDisabledSearchableOnline) {
            return false
        }

        return isEqual
    }, [settingState])

    const onCloseUploadResumeModal = () => {
        setIsShowUpload(false)
        dispatch({ type: ACTION_SHOW_SETTING_PROFILE, payload: false })
        isFromUploadResumeRef.current = true
    }

    const onCancelUploadResumeModal = () => {
        if (typeof window === 'undefined') {
            return
        }
        window.location.reload()
    }

    const getRefresh = async () => {
        // Do not call apiGetRefresh when accessToken is not provided
        if (!user?.accessToken) {
            return
        }
        const response = await apiGetRefresh(user?.accessToken)

        if (response.meta?.status === 200) {
            const date = new Date()
            date.setDate(date.getDate() + 15)

            disPatchSettingState({
                ...settingState,
                expiredDate: convertDateTime(response.data?.attributes?.expiredDate || date, 7),
                statusRefresh: response.data?.attributes?.type ?? 0,
                isTurnOnRefresh: response.data?.attributes?.isTurnOn === 1
            })
        }
    }

    useEffect(() => {
        if (!isOpen) {
            return
        }
        const { attachInfo, profileInfo } = profileSettingInfo.resumeSetting

        let currentSearchableResumeId = profileInfo.isSearchable === 1 ? profileInfo.id : (priorityResume?.id ?? 0)
        // Profile online is not searchable then maybe attach is searchable
        if (profileInfo.isSearchable === 0) {
            const filteredAttach = attachInfo.filter((item) => item.isSearchable === 1)
            if (filteredAttach.length > 0) {
                currentSearchableResumeId = filteredAttach[0].id
            }
        }
        if (priorityResume?.id && typeof priorityResume.id === 'number') {
            const settingValue = {
                resumeId: currentSearchableResumeId,
                statusRefresh: resumeSettingOption.chooseRefreshType,
                expiredDate: ''
            }
            disPatchSettingState({
                ...settingValue,
                ...settingState
            })
            settingRef.current = {
                ...settingValue,
                isOpenToWork: settingRef.current?.isOpenToWork ?? settingState.isOpenToWork,
                isTurnOnRefresh: settingRef.current?.isTurnOnRefresh ?? settingState.isTurnOnRefresh
            }
        }
        if (memoConfigPopupUpload.showUploadResume) {
            setIsShowUpload(isOpen)
        } else {
            setIsShowSetting(isOpen)
        }
    }, [isOpen, memoConfigPopupUpload, priorityResume])

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current)
            return
        }

        const tab = router.query.tab
        if (tab !== PROFILE_SETTINGS_TAB) return

        timer.current = setTimeout(() => {
            dispatch({
                type: ACTION_SHOW_SETTING_PROFILE,
                payload: true
            })
            router.replace(router.pathname, undefined, { shallow: true })
        }, DELAY_TIME_TO_SHOW_POPUP)

        return () => {
            onClose()
            clearTimeout(timer.current)
        }
    }, [])

    useEffect(() => {
        const onCloseProfileSettingPopup = () => {
            setIsOpenUploadCV(true)
            setAttachCVStep(2)
            onClose()
        }

        window.addEventListener('CLOSE_PROFILE_SETTING_POPUP', onCloseProfileSettingPopup)

        return () => {
            window.removeEventListener('CLOSE_PROFILE_SETTING_POPUP', onCloseProfileSettingPopup)
        }
    }, [])

    useEffect(() => {
        if (!user?.accessToken) {
            return
        }
        getRefresh()
    }, [user?.accessToken])

    useEffect(() => {
        if (userProfileInfo.userId === 0) {
            return
        }
        const currentIsOpenToWork = userProfileInfo.isOpenToWork === 1

        disPatchSettingState({
            ...settingState,
            isOpenToWork: currentIsOpenToWork
        })
    }, [userProfileInfo])

    return (
        <ProfileSettingProvider state={settingState} setState={disPatchSettingState}>
            {children}

            <Popup
                isShow={isShowSetting}
                lang={langCode}
                text={{ header: t('Profile Settings'), secondaryBtn: t('Cancel'), primaryBtn: t('Searchable') }}
                onClose={onClose}
                onClickOutside={onClose}
                onClickPrimary={onClickAllowSearchable}
                option={{
                    disabledPrimary: isDisableSearchableButton
                }}
                custom={{
                    additionalFooterHeight: 0
                }}
            >
                <Row container={{ sm: 16 }}>
                    <Col>
                        <ProfileSettings />
                    </Col>
                </Row>
            </Popup>

            <Popup
                isShow={isShowUncontactable}
                onClose={() => setIsShowUncontactable(false)}
                onClickPrimary={() => setIsShowUncontactable(false)}
                onClickOutside={() => setIsShowUncontactable(false)}
                lang={langCode}
                styleOption={{ size: 'sm' }}
                text={{
                    header: t('Searchable CV is disable')
                }}
                option={{
                    isShowSecondaryButton: false
                }}
            >
                <WarningSearchableModal>
                    {t(
                        'Your searchable CV is disable now because of wrong contact information. Please contact with Vietnamworks to verify your information by email:'
                    )}
                    &nbsp;
                    <a href='mailto:contact@vietnamworks.com'>contact@vietnamworks.com</a>
                </WarningSearchableModal>
            </Popup>

            <UploadResumeModal
                isOpen={isShowUpload}
                onClose={onCloseUploadResumeModal}
                onCancel={onCancelUploadResumeModal}
                jobLevelOptions={jobLevelList.filter((item) => item.id !== -1)}
                jobLocationOptions={locationList}
                jobIndustryOptions={initialDataProvider.getIndustries()}
                jobFunctionOptions={initialDataProvider.getJobFunction()}
                nationalityOptions={nationalityOptions}
                countryOptions={countryOptions}
                cityOptions={cityOptions}
                districtOptions={districtOptions}
                accessToken={user?.accessToken}
                apiAutoCompleteJobTitle={`/api-gateway/v1.0/suggest-job-title`}
                baseApiUrl={process.env.NEXT_PUBLIC_MS_BASE_PATH}
                lang={langCode}
                stepJump={memoConfigPopupUpload.step}
                resumeId={profileInfo.id}
                checkDoneStep={onSyncDataToStoreWhenDoneStep}
                isReplace
                enableJobFunction={ENABLE_JOB_FUNCTION}
            />
            {/* For missing information */}
            <UploadResumeModal
                isOpen={isOpenUploadCV}
                onTogglePopup={(isOpen) => setIsOpenUploadCV(isOpen)}
                isReloadOnClose={true}
                onClose={() => setIsOpenUploadCV(false)}
                jobLevelOptions={jobLevelList.filter((item) => item.id !== -1)}
                jobLocationOptions={locationList}
                jobIndustryOptions={initialDataProvider.getIndustries()}
                jobFunctionOptions={initialDataProvider.getJobFunction()}
                nationalityOptions={nationalityOptions}
                countryOptions={countryOptions}
                cityOptions={cityOptions}
                districtOptions={districtOptions}
                accessToken={user?.accessToken}
                apiAutoCompleteJobTitle={`/api-gateway/v1.0/suggest-job-title`}
                baseApiUrl={process.env.NEXT_PUBLIC_MS_BASE_PATH}
                lang={langCode}
                stepJump={attachCVStep}
                hiddenStep={attachCVStep - 1}
                resumeId={profileInfo.id}
                isReplace
                enableJobFunction={ENABLE_JOB_FUNCTION}
            />
        </ProfileSettingProvider>
    )
}
export default memo(ProfileSettingContainer)
